import { faChevronLeft, faClose } from '@fortawesome/pro-solid-svg-icons';
import type { ReactNode } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage, useIntl } from 'react-intl';
import type { To } from 'react-router';
import { useLocation, useNavigate } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { IconButton } from 'atoms/buttons/IconButton';
import { Portal } from 'atoms/layout/Portal';
import { Horizontal } from 'atoms/layout/flex';
import { HeadlineL } from 'atoms/typography';
import { useSportContext } from 'contexts/sport';
import { useIsLoggedIn } from 'hooks/auth/useIsLoggedIn';
import { useIsLaptop } from 'hooks/device/useIsLaptop';
import { useEnableSeriesNavigationUpdate } from 'hooks/navigation/useEnableSeriesNavigationUpdate';
import { useDeeplinkFromNativeApp } from 'hooks/useDeeplinkFromNativeApp';
import { useIsMobileApp } from 'hooks/useIsMobileApp';
import { glossary } from 'lib/glossary';
import { hasPreviousLocation } from 'lib/routing';

import { UserCurrencies } from '../AppNavigation/UserCurrencies';
import { UserNav } from '../UserNav';
import { useExpandedCurrencies } from './useExpandedCurrencies';

const StyledLink = styled(Link)`
  overflow: hidden;

  &:hover,
  &:focus,
  &:active {
    color: inherit;
  }
`;

const Title = styled(HeadlineL)`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const SecondRow = styled(Horizontal).attrs({ gap: 0 })`
  padding: 0 var(--double-unit) 0 var(--intermediate-unit);
  border-bottom: 1px solid var(--c-nd-50);
`;

type Props = {
  text?: MessageDescriptor | string;
  children?: ReactNode;
  backTo?: To;
  disableBackBrowser?: boolean;
  onClick?: () => void;
  customTitle?: ReactNode;
};
export const PageHeader = ({
  text,
  children,
  backTo,
  onClick,
  disableBackBrowser,
  customTitle,
}: Props) => {
  const { sport } = useSportContext();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { isIosApp } = useIsMobileApp();
  const isLaptopAndAbove = useIsLaptop();
  const isLoggedIn = useIsLoggedIn();
  const { navigateWithDeeplink } = useDeeplinkFromNativeApp();
  const location = useLocation();
  const expandedCurrencies = useExpandedCurrencies();
  const enableSeriesNavigationUpdate = useEnableSeriesNavigationUpdate();

  if (!isLoggedIn) {
    return null;
  }

  // back browser can be disabled using prop or
  // is always disabled if user comes from global NavItem
  const shouldDisableBackBrowser =
    disableBackBrowser || location.state?.fromNavItem;

  const backButton =
    backTo ||
    (!shouldDisableBackBrowser && hasPreviousLocation(-1)) ||
    onClick ? (
      <IconButton
        size="medium"
        color="transparent"
        onClick={() => {
          if (onClick) {
            onClick();
          } else if (backTo) {
            navigate(backTo);
          } else {
            navigate(-1);
          }
        }}
        icon={faChevronLeft}
        aria-label={formatMessage(glossary.back)}
      />
    ) : (
      <>
        {!!isIosApp && (
          <IconButton
            size="medium"
            color="transparent"
            onClick={() => {
              navigateWithDeeplink();
            }}
            icon={faClose}
            aria-label={formatMessage(glossary.close)}
          />
        )}
      </>
    );

  const title = !!text && (
    <StyledLink
      to="."
      onClick={e => {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }}
    >
      <Title as="p">
        {typeof text === 'string' ? text : <FormattedMessage {...text} />}
      </Title>
    </StyledLink>
  );

  if (enableSeriesNavigationUpdate && !isLaptopAndAbove) {
    return (
      <Portal id="page-header-second-row">
        <SecondRow gap={0}>
          {backButton}
          {title}
        </SecondRow>
      </Portal>
    );
  }

  const displayUserProfile = shouldDisableBackBrowser && !isLaptopAndAbove;

  return (
    <Portal id="page-header-title">
      {displayUserProfile ? (
        <Horizontal>
          <UserNav sport={sport} />
          <UserCurrencies expandedCurrencies={expandedCurrencies} />
        </Horizontal>
      ) : (
        children || (
          <Horizontal>
            {backButton}
            {title}
            {customTitle}
          </Horizontal>
        )
      )}
    </Portal>
  );
};
